import type {
  OrderCategory,
  Period,
  RangeErrors,
  TrackerShoppingListItem,
  Translate,
} from './types';

/** Longest range the API accepts, both ends inclusive. */
export const MAX_RANGE_DAYS = 90;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 24 * 60 * 60 * 1000;

const pad = (value: number) => String(value).padStart(2, '0');

export const formatItemName = ({ name, brand }: Pick<TrackerShoppingListItem, 'name' | 'brand'>) =>
  brand ? `${name} (${brand})` : name;

/** Two decimals — same rounding as the meal-plan `MeasurementUnits` for weights and volumes. */
export const roundAmount = (amount: number) => Math.round(amount * 100) / 100;

const formatUnit = (item: TrackerShoppingListItem, t: Translate) =>
  t(`meal.ingredient.unit.${item.baseUnit}`, { count: roundAmount(item.baseAmount) });

/** Adapts tracker items to the category shape the meal-plan order buttons expect. */
export const toOrderCategories = (
  items: TrackerShoppingListItem[],
  t: Translate
): OrderCategory[] => [
  {
    products: items.map((item) => ({
      name: formatItemName(item),
      isBought: item.isBought,
      measurements: [{ amount: roundAmount(item.baseAmount), text: formatUnit(item, t) }],
    })),
  },
];

export const toIsoDate = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

export const defaultPeriod = (today: Date): Period => {
  const end = new Date(today);
  end.setDate(end.getDate() + 6);
  return { start: toIsoDate(today), end: toIsoDate(end) };
};

/** Parses `YYYY-MM-DD` to a UTC timestamp; null for malformed or impossible dates. */
const toUtcDay = (value: string): number | null => {
  if (!ISO_DATE.test(value)) return null;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCMonth() === month - 1 && date.getUTCDate() === day ? date.getTime() : null;
};

export const validateRange = ({ start, end }: Period, t: Translate): RangeErrors => {
  const details: RangeErrors['details'] = [];
  const startDay = toUtcDay(start);
  const endDay = toUtcDay(end);

  if (startDay === null) {
    details.push({ fieldName: 'start', messages: [t('shoppingList.validation.dateFormat')] });
  }
  if (endDay === null) {
    details.push({ fieldName: 'end', messages: [t('shoppingList.validation.dateFormat')] });
  }
  if (startDay === null || endDay === null) return { details };

  if (startDay > endDay) {
    details.push({ fieldName: 'end', messages: [t('tracker.shoppingList.validation.dateOrder')] });
  } else if ((endDay - startDay) / DAY_MS + 1 > MAX_RANGE_DAYS) {
    details.push({ fieldName: 'end', messages: [t('tracker.shoppingList.validation.maxRange')] });
  }

  return { details };
};

export const hasRangeErrors = (errors: RangeErrors) => errors.details.length > 0;
